import React, { useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import '../components/Dashboard.css';

const QuizModal = ({ isOpen, onClose, selectedDocument, user }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [quizOptions, setQuizOptions] = useState({
        length: 'medium',
        difficulty: 'intermediate',
        style: 'engaging',
        questionTypes: ['multiple_choice']
    });
    const [quizLoading, setQuizLoading] = useState(false);
    const [quizResponse, setQuizResponse] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);

    const generateQuiz = async () => {
        if (!selectedDocument || quizLoading) return;

        setQuizLoading(true);
        setCurrentQuestionIndex(0);
        setSelectedAnswers({});
        setShowResults(false);

        try {
            const response = await fetch('https://rag-study-backend-production.up.railway.app/generate_quiz', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user?.uid,
                    document_id: selectedDocument?.id || selectedDocument?.document_id || selectedDocument?.uuid,
                    quiz_options: quizOptions
                })
            });

            if (!response.ok) {
                throw new Error('Failed to generate quiz');
            }

            const data = await response.json();
            setQuizResponse(data);
        } catch (error) {
            console.error('Quiz generation error:', error);
            // Handle error - could show a toast or error message
        } finally {
            setQuizLoading(false);
        }
    };

    const handleAnswerSelect = (questionId, answer) => {
        setSelectedAnswers(prev => ({
            ...prev,
            [questionId]: answer
        }));
    };

    const nextQuestion = () => {
        if (quizResponse && currentQuestionIndex < quizResponse.questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const prevQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const submitQuiz = () => {
        setShowResults(true);
    };

    if (!isOpen) return null;

    return (
        <div className="quiz-modal-overlay">
            <div className="quiz-modal">
                <div className="quiz-modal-header">
                    <h2>Tell us a bit more about how you want it?</h2>
                    <div className="dashboard-progress-bar">
                        <div className="progress-step active">1</div>
                        <div className="progress-step active">2</div>
                        <div className="progress-step">3</div>
                    </div>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>
                
                <div className="quiz-modal-content">
                    {/* Quiz Length */}
                    <div className="quiz-option-section">
                        <h3>Quiz Length</h3>
                        <div className="option-grid">
                            {[
                                { value: 'short', label: 'Short', range: '1-10 questions' },
                                { value: 'medium', label: 'Medium', range: '1-35 questions' },
                                { value: 'long', label: 'Long', range: '1-50 questions' }
                            ].map(option => (
                                <div 
                                    key={option.value}
                                    className={`option-card ${quizOptions.length === option.value ? 'selected' : ''}`}
                                    onClick={() => setQuizOptions(prev => ({ ...prev, length: option.value }))}
                                >
                                    <div className="option-label">{option.label}</div>
                                    <div className="option-range">{option.range}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Difficulty Level */}
                    <div className="quiz-option-section">
                        <h3>What Level?</h3>
                        <div className="option-grid">
                            {[
                                { value: 'beginner', label: 'Beginner', icon: '😊' },
                                { value: 'intermediate', label: 'Intermediate', icon: '📖' },
                                { value: 'advanced', label: 'Advanced', icon: '🏆' }
                            ].map(option => (
                                <div 
                                    key={option.value}
                                    className={`option-card ${quizOptions.difficulty === option.value ? 'selected' : ''}`}
                                    onClick={() => setQuizOptions(prev => ({ ...prev, difficulty: option.value }))}
                                >
                                    <div className="option-label">{option.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Style */}
                    <div className="quiz-option-section">
                        <h3>What style you prefer?</h3>
                        <div className="option-grid">
                            {[
                                { value: 'genz', label: 'Gen Z', icon: '😄' },
                                { value: 'scholar', label: 'Scholar', icon: '🎓' },
                                { value: 'engaging', label: 'Engaging', icon: '⚡' }
                            ].map(option => (
                                <div 
                                    key={option.value}
                                    className={`option-card ${quizOptions.style === option.value ? 'selected' : ''}`}
                                    onClick={() => setQuizOptions(prev => ({ ...prev, style: option.value }))}
                                >
                                    <div className="option-label">{option.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Question Types */}
                    <div className="quiz-option-section">
                        <h3>What types of questions would you like?</h3>
                        <div className="option-grid">
                            {[
                                { value: 'true_false', label: 'True/False', icon: '✅' },
                                { value: 'multiple_choice', label: 'Multiple Choice', icon: '☑️' },
                                { value: 'fill_blank', label: 'Fill in the Blanks', icon: '✏️' },
                                { value: 'match_pairs', label: 'Match Pairs', icon: '↔️' }
                            ].map(option => (
                                <div 
                                    key={option.value}
                                    className={`option-card ${quizOptions.questionTypes.includes(option.value) ? 'selected' : ''}`}
                                    onClick={() => {
                                        setQuizOptions(prev => {
                                            const newTypes = prev.questionTypes.includes(option.value)
                                                ? prev.questionTypes.filter(t => t !== option.value)
                                                : [...prev.questionTypes, option.value];
                                            return { ...prev, questionTypes: newTypes };
                                        });
                                    }}
                                >
                                    <div className="option-label">{option.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="quiz-modal-footer">
                    <div className="file-info">1 File</div>
                    <button 
                        className="continue-button"
                        onClick={() => {
                            onClose();
                            generateQuiz();
                        }}
                        disabled={quizOptions.questionTypes.length === 0}
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuizModal;
